import { resource_types } from 'store/extraResource/constants';
import { extenionGetPageData, extensionGetBookData, getBookData } from 'utils/lib/ebookHelper';

export const RESOURCE_BOOK_ID_MAP = {
  [resource_types.balcon]: 33,
  [resource_types.conectándome]: 49,
  [resource_types.comunidad]: 43,
  [resource_types.informate]: 41,
  [resource_types.story]: 255,
};

export const generatePageRange = (start, finish) => {
  const pages = [];
  const startNum = Number(start);
  const finishNum = Number(finish);

  if (isNaN(startNum) || isNaN(finishNum) || startNum > finishNum) {
    return [];
  }

  for (let i = startNum; i <= finishNum; i++) {
    pages.push(i.toString());
  }
  return pages;
};

export const mergePages = (existingPages, newPages) => {
  if (!existingPages) return newPages;
  return [...existingPages, ...newPages];
};

export const findMatchingLevel = (levels, targetLevel) => {
  if (!levels || !targetLevel) return null;

  const normalizedTarget = targetLevel.toLowerCase().trim();
  return levels.find((level) => level?.level?.toLowerCase().trim() === normalizedTarget);
};

export const processAPResource = (element, bookWithPageId) => {
  const bookId = element?.config?.selectedCategoryAP?.chapter_ids;
  const { start, finish } = element?.config?.selectedItems?.[0] || {};

  if (!bookId || !start || !finish) {
    return;
  }

  const pages = generatePageRange(start, finish);
  bookWithPageId[bookId] = mergePages(bookWithPageId[bookId], pages);
};

export const processStandardResource = (element, bookWithPageId) => {
  const bookId = RESOURCE_BOOK_ID_MAP[element.resource_type];
  const { start, finish } = element?.config?.selectedItems?.[0] || {};

  if (!bookId || !start || !finish) {
    return;
  }

  const pages = generatePageRange(start, finish);
  bookWithPageId[bookId] = mergePages(bookWithPageId[bookId], pages);
};

export const processResourceType = (element, bookWithPageId) => {
  const bookId = element?.config?.selectedItems?.[0]?.id;

  if (!bookId) {
    return;
  }

  bookWithPageId[bookId] = [bookId.toString()];
};

export const processStoryResource = (element, bookWithPageId) => {
  const bookId = Number(element?.config?.selectedItems?.[0]?.bookId);
  const configLevel = element?.config?.level;
  const levels = element?.config?.selectedItems?.[0]?.levels;

  if (!bookId || !configLevel || !levels) {
    console.warn('Invalid story resource configuration:', element);
    return;
  }

  const matchedLevel = findMatchingLevel(levels, configLevel);

  if (!matchedLevel) {
    console.warn(
      `No matching level found for "${configLevel}". Available levels:`,
      levels.map((l) => l?.level),
    );
    return;
  }

  const { start, finish } = matchedLevel;

  if (!start || !finish) {
    console.warn('Invalid start/finish values in matched level:', matchedLevel);
    return;
  }

  const pages = generatePageRange(start, finish);
  bookWithPageId[bookId] = mergePages(bookWithPageId[bookId], pages);
};

// Main resource processor
export const processResource = (element, bookWithPageId) => {
  const { resource_type } = element;
  const { AP, informate, balcon, conectándome, comunidad, resource, story } = resource_types;

  try {
    switch (resource_type) {
      case AP:
        processAPResource(element, bookWithPageId);
        break;
      case balcon:
      case conectándome:
      case comunidad:
      case informate:
        processStandardResource(element, bookWithPageId);
        break;
      case resource:
        processResourceType(element, bookWithPageId);
        break;
      case story:
        processStoryResource(element, bookWithPageId);
        break;
      default:
        return;
    }
  } catch (error) {
    console.error('Error processing resource:', error, element);
  }
};

// Filter functions
export const filterActivities = (activities, configExtraResource) => {
  if (!activities?.length) return [];

  return activities.filter((item) => {
    const bookId = item?.data?.bookId?.toString();
    const page = item?.data?.page?.toString();

    if (!bookId || !page) {
      return false;
    }
    // Check if this bookId exists in our config
    const configPages = configExtraResource[bookId];
    if (!configPages) {
      return false;
    }
    const pageExists = configPages.includes(page);
    return pageExists;
  });
};

export const filterQuizzes = (quizzes, configExtraResource) => {
  if (!quizzes?.length) return [];

  return quizzes.filter((item) => {
    const bookId = item?.data?.bookId?.toString();
    const page = item?.data?.page?.toString();

    if (!bookId || !page) {
      return false;
    }

    // Check if this bookId exists in our config
    const configPages = configExtraResource[bookId];
    if (!configPages) {
      return false;
    }

    // Check if the page is in the configured pages for this bookId
    const pageExists = configPages.includes(page);
    return pageExists;
  });
};

export function combineBookIdsAndPageIds(data) {
  const combinedBookIds = [];
  const combinedPageIds = [];

  // Iterate through each book and its pages
  Object.entries(data).forEach(([bookId, pageIds]) => {
    // For each page in this book, add the bookId-pageId pair
    pageIds.forEach((pageId) => {
      combinedBookIds.push(bookId);
      combinedPageIds.push(pageId);
    });
  });

  return [combinedBookIds, combinedPageIds];
}

export const modifyUrlParams = (urlPath, newParams) => {
  const url = new URL(urlPath, window.location.origin);
  const searchParams = new URLSearchParams(url.search);

  // Set/update the parameters
  Object.entries(newParams).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.set(key, value);
    } else {
      searchParams.set(key, 'null');
    }
  });

  // Return the modified URL
  return `${url.pathname}?${searchParams.toString()}`;
};

export function combineBookIdsAndPageIdsWithUnique(data) {
  const uniqueBookIds = Object.keys(data);
  const allPageIds = Object.values(data).flat();

  return [uniqueBookIds, allPageIds];
}

export const getBookDataByID = (bookState, bookId, isAssessmentBook) => {
  const data = getBookData(
    bookState.activities,
    bookState.quizzes,
    bookState.questions,
    bookState.notes,
    bookState.likes,
    bookState.interactions,
    bookState.classActivities,
    bookState.classQuizzes,
    bookId,
    bookState.currentClass.id,
    false,
    bookState.currentUser.id,
  );
  return data;
};

export const getExtensionBookDataById = (bookState, bookId, isAssessmentBook, combinedBookIds) => {
  const data = extensionGetBookData(
    bookState.activities,
    bookState.quizzes,
    bookState.questions,
    bookState.notes,
    bookState.likes,
    bookState.interactions,
    bookState.classActivities,
    bookState.classQuizzes,
    bookId,
    bookState.currentClass.id,
    isAssessmentBook,
    bookState.currentUser.id,
    combinedBookIds,
  );
  return data;
};

export const getExtensionPageDataByPageId = (bookState, bookData, pageId,combinedPageIds, configExtensionData,bookId,from) => {
  const data= extenionGetPageData(
    bookData,
    pageId,
    bookState?.quiz,
    bookState?.activity,
    null,
    bookId,
    configExtensionData,
    from
  );
  return data;
};
